import React, {useState, useEffect} from 'react';
import {Calendar as BigCalendar, dateFnsLocalizer, View, Views} from 'react-big-calendar';
import {format, parse, startOfWeek, getDay, addMonths, subMonths, addWeeks, subWeeks, addDays, subDays} from 'date-fns';
import {utcToZonedTime} from 'date-fns-tz';
import {Star, Plus} from 'lucide-react';
import {useAuth} from '../contexts/AuthContext';
import './calendar.css';
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
} from 'firebase/firestore';
import {db} from '../firebase';
import type {TimeSlot, StarAvailabilitySettings} from '../types/availability';
import SettingsForm from '../components/SettingsForm';
import AddSlotModal from '../components/AddSlotModal';
import SlotActionModal from '../components/SlotActionModal';
import 'react-big-calendar/lib/css/react-big-calendar.css';

const locales = {
  'en-US': require('date-fns/locale/en-US'),
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const cn = (...args: any[]): string => {
  let classes = '';
  for (const arg of args) {
    if (typeof arg === 'string' && arg) {
      classes += (classes ? ' ' : '') + arg;
    } else if (typeof arg === 'object' && arg !== null) {
      for (const key in arg) {
        if (Object.prototype.hasOwnProperty.call(arg, key) && arg[key]) {
          classes += (classes ? ' ' : '') + key;
        }
      }
    }
  }
  return classes;
};

const ManageAvailabilityScreen: React.FC = () => {
  const {user} = useAuth();
  const [settings, setSettings] = useState<StarAvailabilitySettings>({
    minCallDuration: 5,
    maxCallDuration: 30,
    pricePerMinute: 1,
    minBidIncrement: 5,
    allowBidding: true,
  });
  const [slots, setSlots] = useState<TimeSlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isActionModalOpen, setIsActionModalOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [calendarView, setCalendarView] = useState<View>('month');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [editingSlot, setEditingSlot] = useState<TimeSlot | null>(null);
  const [userTimezone] = useState<string>(Intl.DateTimeFormat().resolvedOptions().timeZone);

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;
      setLoading(true);
      try {
        const settingsDoc = await getDoc(doc(db, 'users', user.uid));
        if (settingsDoc.exists()) {
          const userData = settingsDoc.data();
          setSettings({
            minCallDuration: userData.minCallDuration || 5,
            maxCallDuration: userData.maxCallDuration || 30,
            pricePerMinute: userData.pricePerMinute || 1,
            minBidIncrement: userData.minBidIncrement || 5,
            allowBidding: userData.allowBidding !== undefined ? userData.allowBidding : true,
          });
        }
        await refreshSlots();
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  const refreshSlots = async () => {
    if (!user) return;

    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);

    // Query by startTime range (uses existing composite index on starId + startTime)
    // Include slots from 1 day ago to catch active/cross-midnight slots
    const oneDayAgo = new Date();
    oneDayAgo.setDate(oneDayAgo.getDate() - 1);

    const slotsQuery = query(
      collection(db, 'slots'),
      where('starId', '==', user.uid),
      where('startTime', '>=', oneDayAgo),
      where('startTime', '<=', thirtyDaysFromNow),
    );

    const slotsSnapshot = await getDocs(slotsQuery);
    const slotsData: TimeSlot[] = [];

    slotsSnapshot.forEach(docSnap => {
      const data = docSnap.data();
      slotsData.push({
        id: docSnap.id,
        ...data,
        startTime: data.startTime.toDate(),
        endTime: data.endTime.toDate(),
        bookings: (data.bookings || []).map((b: any) => ({
          ...b,
          startTime: b.startTime?.toDate ? b.startTime.toDate() : new Date(b.startTime),
          endTime: b.endTime?.toDate ? b.endTime.toDate() : new Date(b.endTime),
          createdAt: b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt),
        })),
      } as TimeSlot);
    });

    setSlots(slotsData);
  };

  const handleSettingsUpdate = (newSettings: StarAvailabilitySettings) => {
    setSettings(newSettings);
  };

  const handleSlotAdded = async () => {
    await refreshSlots();
  };

  const handleSelectSlot = ({start}: {start: Date}) => {
    const now = new Date();
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);

    if (start < now || start > thirtyDaysFromNow) {
      return;
    }

    setSelectedDate(start);
    setEditingSlot(null);
    setIsAddModalOpen(true);
  };

  const handleSelectEvent = (event: any) => {
    setSelectedSlot(event.resource);
    setIsActionModalOpen(true);
  };

  const handleModifySlot = () => {
    if (selectedSlot) {
      setEditingSlot(selectedSlot);
      setSelectedDate(selectedSlot.startTime);
      setIsActionModalOpen(false);
      setIsAddModalOpen(true);
    }
  };

  const handleNavigate = (date: Date) => {
    setSelectedDate(date);
  };

  const handleViewChange = (view: View) => {
    setCalendarView(view);
  };

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100 px-4 py-8 text-gray-800">
        <p>Please login to access this page.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <main className="container mx-auto max-w-4xl py-6 px-4 sm:px-6 lg:px-8 space-y-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6">Manage Availability</h1>
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500" />
          </div>
        ) : (
          <>
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
              <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                <h3 className="text-lg leading-6 font-medium text-gray-900">Global Settings</h3>
              </div>
              <div className="p-4 sm:p-6">
                <SettingsForm
                  settings={settings}
                  userId={user.uid}
                  onUpdate={handleSettingsUpdate}
                />
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
              <div className="px-4 py-5 sm:px-6 border-b border-gray-200 flex justify-between items-center">
                <div>
                  <h3 className="text-lg leading-6 font-medium text-gray-900">Available Slots</h3>
                  <p className="text-sm text-gray-500 mt-1">Times shown in your timezone ({userTimezone})</p>
                </div>
                <button
                  onClick={() => {
                    setEditingSlot(null);
                    setIsAddModalOpen(true);
                  }}
                  className="flex items-center gap-1.5 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold py-2 px-3 rounded-md text-sm shadow-sm transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  Add Slot
                </button>
              </div>
              <div className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-2">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleNavigate(new Date())}
                      className="px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                    >
                      Today
                    </button>
                    <button
                      onClick={() => {
                        let newDate;
                        if (calendarView === Views.MONTH) newDate = subMonths(selectedDate, 1);
                        else if (calendarView === Views.WEEK) newDate = subWeeks(selectedDate, 1);
                        else newDate = subDays(selectedDate, 1);
                        handleNavigate(newDate);
                      }}
                      className="px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                    >
                      Back
                    </button>
                    <button
                      onClick={() => {
                        let newDate;
                        if (calendarView === Views.MONTH) newDate = addMonths(selectedDate, 1);
                        else if (calendarView === Views.WEEK) newDate = addWeeks(selectedDate, 1);
                        else newDate = addDays(selectedDate, 1);
                        handleNavigate(newDate);
                      }}
                      className="px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                    >
                      Next
                    </button>
                  </div>
                  <div className="flex items-center rounded-md border border-gray-300">
                  {(Object.keys(Views) as Array<keyof typeof Views>).map((key, index) => {
                      const view = Views[key];
                      if (view === Views.AGENDA || view === Views.WORK_WEEK) return null;
                      return (
                        <button
                          key={view}
                          onClick={() => handleViewChange(view)}
                          className={cn(
                            "px-3 py-1.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-purple-500",
                            "first:rounded-l-md last:rounded-r-md",
                            (view === Views.WEEK || view === Views.DAY) && "border-l border-gray-300",
                            calendarView === view
                              ? "bg-gray-100 text-gray-700"
                              : "text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                          )}
                        >
                          {view.charAt(0).toUpperCase() + view.slice(1)}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="rounded-md border border-gray-200 overflow-hidden h-[400px] sm:h-[600px]">
                  <BigCalendar
                    localizer={localizer}
                    events={slots.flatMap(slot => {
                      let slotLabel = 'Available';
                      if (slot.bookings?.length) {
                        const totalMinutes = (slot.endTime.getTime() - slot.startTime.getTime()) / 60000;
                        const bookedMinutes = slot.bookings.reduce((sum, b: any) => {
                          const bStart = b.startTime instanceof Date ? b.startTime : b.startTime.toDate ? b.startTime.toDate() : new Date(b.startTime);
                          const bEnd = b.endTime instanceof Date ? b.endTime : b.endTime.toDate ? b.endTime.toDate() : new Date(b.endTime);
                          return sum + (bEnd.getTime() - bStart.getTime()) / 60000;
                        }, 0);
                        slotLabel = bookedMinutes >= totalMinutes ? 'Fully Booked' : 'Partially Booked';
                      }
                      const baseEvent = {
                        title: slotLabel,
                        start: slot.startTime,
                        end: slot.endTime,
                        resource: slot,
                      };
                      // Only show individual "Booked" sub-events in week/day view (not month)
                      if (slot.bookings?.length && calendarView !== 'month') {
                        return [
                          baseEvent,
                          ...slot.bookings.map(booking => ({
                            title: 'Booked',
                            start: booking.startTime,
                            end: booking.endTime,
                            resource: slot,
                          })),
                        ];
                      }
                      return [baseEvent];
                    })}
                    startAccessor="start"
                    endAccessor="end"
                    style={{height: '100%'}}
                    view={calendarView}
                    onView={handleViewChange}
                    date={selectedDate}
                    onNavigate={handleNavigate}
                    selectable
                    onSelectSlot={handleSelectSlot}
                    onSelectEvent={handleSelectEvent}
                    eventPropGetter={(event:any) => ({
                      className: `rounded-md p-1 text-xs ${
                        event.title === 'Booked'
                          ? 'bg-slate-400 text-white border border-slate-500'
                          : event.title === 'Partially Booked' || event.title === 'Fully Booked'
                          ? 'bg-orange-300 text-orange-800 border border-orange-400'
                          : 'bg-green-300 text-green-800 border border-green-400'
                      }`,
                    })}
                    min={new Date(0, 0, 0, 0, 0, 0)}
                    max={new Date(0, 0, 0, 23, 59, 59)}
                    className="rbc-calendar bg-white"
                    views={['month', 'week', 'day']}
                    popup
                    toolbar={false}
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </main>

      <AddSlotModal
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          setEditingSlot(null);
        }}
        userId={user.uid}
        settings={settings}
        onSlotAdded={handleSlotAdded}
        initialDate={selectedDate}
        editSlot={editingSlot}
      />

      {selectedSlot && (
        <SlotActionModal
          slot={selectedSlot}
          isOpen={isActionModalOpen}
          onClose={() => {
            setIsActionModalOpen(false);
            setSelectedSlot(null);
          }}
          onDelete={handleSlotAdded}
          onModify={handleModifySlot}
        />
      )}
    </div>
  );
};

export default ManageAvailabilityScreen;
